import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { PageHero, Container, Reveal } from "@/components/public/parts";
import { Field, Success, useSubmit, Input, Button, Loader2 } from "./formkit";
import { MapPin, Briefcase, CheckCircle2, Upload } from "lucide-react";

const REQS = ["Compassionate, reliable and team-oriented", "Valid driver's license (role dependent)", "Ability to pass a background check", "High school diploma or equivalent"];
const RESP = ["Provide person-centered daily support", "Follow individual support plans", "Maintain a safe, respectful environment", "Document and communicate clearly"];

export default function JobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [nf, setNf] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({});
  const { loading, result, submit } = useSubmit();

  useEffect(() => { api.get(`/public/jobs/${id}`).then((r) => { setJob(r.data); setForm((f) => ({ ...f, position: r.data.title })); }).catch(() => setNf(true)); }, [id]);

  if (nf) return <Container className="py-24 text-center"><h1 className="font-heading text-3xl font-bold">Position not found</h1><Link to="/careers" className="text-primary mt-4 inline-block">← Back to careers</Link></Container>;
  if (!job) return <div className="py-32 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const onSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("name", form.name || ""); fd.append("email", form.email || ""); fd.append("phone", form.phone || "");
    fd.append("position", job.title); fd.append("job_id", job.id); fd.append("message", form.message || "");
    if (file) fd.append("file", file);
    submit(async () => (await api.post("/public/apply", fd, { headers: { "Content-Type": "multipart/form-data" } })).data);
  };

  return (
    <>
      <Seo title={`${job.title} | Careers at Love For Mankind`} description={job.description?.slice(0, 155)} path={`/careers/${id}`}
        jsonLd={{ "@context": "https://schema.org", "@type": "JobPosting", title: job.title, description: job.description, employmentType: job.type, hiringOrganization: { "@type": "Organization", name: "Love For Mankind" }, jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: job.location } } }} />
      <PageHero eyebrow="Career Opportunity" title={job.title} breadcrumbs={[{ label: "Home", to: "/" }, { label: "Careers", to: "/careers" }, { label: job.title }]} />
      <Container className="py-16 lg:py-24 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {job.location}</span>
            <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4" /> {job.type}</span>
          </div>
          <h2 className="font-heading text-2xl font-bold mt-8">Role overview</h2>
          <p className="text-[17px] text-muted-foreground mt-3 leading-[1.75]">{job.description}</p>
          <h3 className="font-heading text-xl font-semibold mt-8">Responsibilities</h3>
          <ul className="mt-4 space-y-2.5">{RESP.map((r) => <li key={r} className="flex items-center gap-2.5 text-[16px]"><CheckCircle2 className="w-5 h-5 text-primary shrink-0" /> {r}</li>)}</ul>
          <h3 className="font-heading text-xl font-semibold mt-8">Requirements</h3>
          <ul className="mt-4 space-y-2.5">{REQS.map((r) => <li key={r} className="flex items-center gap-2.5 text-[16px]"><CheckCircle2 className="w-5 h-5 text-gold shrink-0" /> {r}</li>)}</ul>
        </div>
        <div className="lg:col-span-1">
          <div className="sticky top-32 rounded-2xl border border-border bg-card p-6">
            <h3 className="font-heading text-xl font-bold">Apply Now</h3>
            {result ? <div className="mt-4"><Success refNo={result.reference_no} title="Application received" message={result.message} /></div> : (
              <form onSubmit={onSubmit} className="space-y-4 mt-4" data-testid="apply-form">
                <Field label="Full Name" required><Input required value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ap-name" /></Field>
                <Field label="Email" required><Input type="email" required value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ap-email" /></Field>
                <Field label="Phone"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ap-phone" /></Field>
                <Field label="Resume / CV (optional)">
                  <label className="flex items-center gap-2 rounded-lg border-2 border-dashed border-border px-3 py-2.5 cursor-pointer hover:border-primary transition-colors">
                    <Upload className="w-4 h-4 text-muted-foreground" /><span className="text-sm text-muted-foreground truncate">{file ? file.name : "Attach file"}</span>
                    <input type="file" className="hidden" accept="image/*,application/pdf,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0])} data-testid="ap-file" />
                  </label>
                </Field>
                <Button type="submit" className="rounded-full w-full h-12" disabled={loading} data-testid="ap-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Application"}</Button>
              </form>
            )}
          </div>
        </div>
      </Container>
    </>
  );
}
